import dayjs from 'dayjs';
import {
  Payment as AirwallexPayment,
  Payouts as AirwallexPayouts,
} from '@airwallex/components-sdk'; // should find solution for this in types
import { DisabledReason } from '../account/account';
import { TemporalUnit } from '../cfo-dashboard/cfo-dashboard';
import {
  IdIdentifier,
  TimeIdentifiers,
  UserIdentifier,
} from '../common/identifiers';
import { AssetClass } from '../program/program';

export enum PayoutStatus {
  Pending = 'pending',
  ProcessingByTradingSystem = 'processing_by_trading_system',
  Cancelled = 'cancelled',
  Paid = 'paid',
  Declined = 'declined',
  Processing = 'processing',
  Approved = 'approved',
  Scheduled = 'scheduled',
  Sent = 'sent',
  Failed = 'failed',
  PendingVerification = 'pending_verification',
}

export interface ITotalExpense {
  temporalUnit: TemporalUnit;
  normalizedEndDate: dayjs.Dayjs;
  normalizedStartDate: dayjs.Dayjs;
  previousNormalizedEndDate: dayjs.Dayjs;
  previousNormalizedStartDate: dayjs.Dayjs;
}

export interface IPayoutsForExportInput {
  startDate: Date;
  endDate: Date;
}

export interface IPayoutInsightsInput {
  adminId: string | null | undefined;
  normalizedEndDate: dayjs.Dayjs;
  normalizedStartDate: dayjs.Dayjs;
}

export const PAYOUT_MINIMUM = 150;

export const MAX_CRYPTO_WITHDRAWAL_LIMIT = 1499;

export const AFFILIATE_PAYMENT_PENDING_DAYS = 14;

export const UNRESTRICTED_WITHDRAWAL_CRYPTO_COUNTRIES = [
  'RU',
  'UA',
  'Russia',
  'Ukraine',
];

export enum PaymentMethod {
  Crypto = 'crypto',
  Rise = 'rise',
  Airwallex = 'airwallex',
  CreditCard = 'credit-card',
  HubCredits = 'hub-credits',
  Wise = 'wise',
}

const commissionPerPaymentMethod: Record<PaymentMethod, number> = {
  [PaymentMethod.Rise]: 3.5,
  [PaymentMethod.Crypto]: 3.5,
  [PaymentMethod.CreditCard]: 3.5,
  [PaymentMethod.HubCredits]: 0,
  [PaymentMethod.Airwallex]: 3.5,
  [PaymentMethod.Wise]: 3.5,
};

export const getPayoutCommission = ({
  paymentMethod,
}: {
  paymentMethod: PaymentMethod;
}): number => {
  return commissionPerPaymentMethod[paymentMethod];
};

export enum CryptoCurrency {
  USDT = 'USDT',
  USDC = 'USDC',
  ETH = 'ETH',
  LTC = 'LTC',
}

export enum ReasonToDecline {
  Fix = 'fix',
  Invalid = 'invalid',
  NotEligble = 'not-eligble',
}

export type PayoutMetadata = {
  email?: string;
  riseId?: string;
  beneficiaryId?: string;
  currency?: string;
  walletAddress?: string;
  cryptoCurrency?: string;
  creditAmount?: number;
  balanceAmount?: number;
  userShare?: number;
  userPayoutAfterCommission?: number;
  commission?: number;
  companyShare?: number;
  fullName?: string;
  paymentIdentifier?: string;
  processedEvents?: (Record<string, string> & {
    status: Uppercase<keyof typeof PayoutStatus>;
  })[];
  tsAccountId?: string;
  assetClass?: AssetClass;
  purchaseId?: string;
  cardHolderName?: string;
  paymentProvider?: string;
};

export type PayoutProcessedBy = {
  adminId: string;
  adminName: string;
};

export enum PayoutSource {
  Affiliate = 'affiliate',
  Account = 'account',
}

export type PayoutSourceTypes = `${PayoutSource}`;

export type Payout = IdIdentifier &
  TimeIdentifiers &
  UserIdentifier & {
    payoutId?: string;
    payoutRefId?: string;
    amount: number;
    sourceId: string;
    status: PayoutStatus;
    type: PayoutSource;
    completedAt?: Date;
    paymentMethod: PaymentMethod;
    metadata?: PayoutMetadata;
    transactionId?: string;
    comment?: string;
    reasonToDecline?: ReasonToDecline;
    refrenceText?: string;
    disableReason?: string | DisabledReason;
  };

export const getPayoutCalculation = ({
  credits,
  amount,
  userPayoutPercentage,
  source,
}: {
  credits: number;
  amount: number;
  userPayoutPercentage: number;
  source: PayoutSource;
}) => {
  if (source === PayoutSource.Affiliate) {
    return {
      userShare: amount,
      companyShare: 0,
      amountToSplit: 0,
      creditsToDeduct: 0,
    };
  }

  let amountToSplit = amount,
    creditsToDeduct = 0;

  if (credits) {
    creditsToDeduct = credits - amount;

    if (creditsToDeduct >= 0) {
      creditsToDeduct = amount;
      amountToSplit = 0;
    } else {
      creditsToDeduct = credits;
      amountToSplit = amount - credits;
    }
  }

  let userShare = 0,
    companyShare = 0;

  if (amountToSplit > 0) {
    userShare = amountToSplit * (userPayoutPercentage / 100);

    companyShare = amountToSplit - userShare;
  }

  return {
    userShare,
    companyShare,
    amountToSplit,
    creditsToDeduct,
  };
};

export type AirwallexAccountAuth = {
  authCode: string;
  codeVerifier: string;
  clientId: string;
  env: AirwallexPayment.AirwallexEnv;
};

export type AirwallexBeneficiaryFormOptions =
  AirwallexPayouts.BeneficiaryFormValue;

export interface AirwallexBeneficiaryFormElement {
  id: string;
}

export const RISE_RESEND_INVITE_TIME_HOURS = 3;

export type PayoutWithProgram = Payout & {
  programId: string;
  programName: string;
};

export type PayoutEnrichedData = {
  tsAccountId?: string;
  assetClass?: AssetClass;
  purchaseId?: string;
  cardHolderName?: string;
  email?: string;
  paymentProvider?: string;
};
